const isPlainObject = value => !!value && typeof value === 'object' && !Array.isArray(value);

const parseJsonBlob = raw => {
  if (raw == null) return raw;
  let data = raw;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch (e) {
      return null;
    }
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (err) {
        return null;
      }
    }
  }
  return data;
};

const looksLikeVariant = value =>
  isPlainObject(value) &&
  (value.variants_options != null ||
    value.variant_label != null ||
    value.variant_id != null ||
    value.variant_name != null);

/**
 * Coerce publicData.variants into an array of variant objects.
 * Accepts a JSON array, a JSON string (including double-encoded), a single
 * variant object, or a map keyed by variant_id.
 */
export const parseVariantsArray = raw => {
  const data = typeof raw === 'string' ? parseJsonBlob(raw) : raw;
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.filter(item => isPlainObject(item));
  }
  if (!isPlainObject(data)) return [];
  if (looksLikeVariant(data)) return [data];
  return Object.values(data).filter(item => isPlainObject(item));
};

export const asListingIdString = value => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (typeof value.uuid === 'string') return value.uuid.trim();
    if (typeof value.id === 'string') return value.id.trim();
    if (value.id && typeof value.id.uuid === 'string') return value.id.uuid.trim();
  }
  return '';
};

const coerceOptionsList = raw => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw.trim());
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const normalizeObjectKeysDeep = value => {
  if (Array.isArray(value)) {
    return value.map(v => normalizeObjectKeysDeep(v));
  }
  if (!isPlainObject(value)) {
    return value;
  }
  return Object.keys(value).reduce((acc, rawKey) => {
    const trimmedKey = typeof rawKey === 'string' ? rawKey.trim() : rawKey;
    acc[trimmedKey] = normalizeObjectKeysDeep(value[rawKey]);
    return acc;
  }, {});
};

const parseSingleVariant = rawVariant => {
  if (!isPlainObject(rawVariant)) return null;

  const normalized = normalizeObjectKeysDeep(rawVariant);
  const variantLabel =
    typeof normalized.variant_label === 'string' ? normalized.variant_label.trim() : '';
  const variantName =
    typeof normalized.variant_name === 'string' ? normalized.variant_name.trim() : '';
  const variantId = asListingIdString(normalized.variant_id);
  const variantType =
    typeof normalized.variant_type === 'string' ? normalized.variant_type.trim() : '';

  const options = coerceOptionsList(normalized.variants_options)
    .map(o => (isPlainObject(o) ? o : null))
    .filter(Boolean)
    .map(o => ({
      productId: asListingIdString(o.product_id),
      optionLabel: typeof o.option_label === 'string' ? o.option_label.trim() : '',
      thumbnailUrl: typeof o.thumbnail_url === 'string' ? o.thumbnail_url.trim() : '',
    }))
    .filter(o => o.productId && o.optionLabel);

  if (!variantLabel || options.length < 2) {
    return null;
  }

  const sortedOptions = [...options].sort((a, b) =>
    a.optionLabel.localeCompare(b.optionLabel, 'fr', { sensitivity: 'base' })
  );

  return { variantId, variantType, variantLabel, variantName, options: sortedOptions };
};

export const parseVariantGroups = variantsRaw =>
  parseVariantsArray(variantsRaw)
    .map(parseSingleVariant)
    .filter(Boolean);
